var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useRef, useState, useEffect } from 'react';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import hive from '@bombas/hive';
import { useTrackInteractedSlide } from '../../ResponsiveImageGallery.hooks';
import InView from '../../../../../Carousel/InView';
import ProductImageZoom from '../../../ProductImageZoom/ProductImageZoom';
import * as Styled from './ImageCarouselLarge.styled';
/**
 * Component that renders peeking Responsive Images (excluding the last image) in a Carousel
 * Logic heavily based off on Carousel.js
 *
 * TO-DO:
 *  Fix tabbing (big issue with peeking)
 *  Add a max height of 80vh
 */
var ImageCarouselLarge = function (_a) {
    var images = _a.images;
    var carouselRef = useRef(null);
    var _b = useState([0]), currentSlidesInView = _b[0], setSlideInView = _b[1];
    var _c = useState(false), hasSnappingRule = _c[0], setHasSnappingRule = _c[1];
    var _d = useState(false), isCarouselRefLoaded = _d[0], setIsCarouselRefLoaded = _d[1];
    var _e = useState(false), isZoomModalOpen = _e[0], setZoomModalOpen = _e[1];
    var _f = useState(images), zoomedImages = _f[0], setZoomedImages = _f[1];
    var _g = useState(false), isPointer = _g[0], setIsPointer = _g[1];
    // prevents NaN from being passed to SelectCarousel
    // we need to add 1 to currentSlide as it is 0-indexed
    var currentSlide = currentSlidesInView[0] ? currentSlidesInView[0] + 1 : 1;
    var imageIds = images.map(function (image) { return image.id.toString(); });
    var handleInViewChange = function (slide, isInView) {
        setSlideInView(function (state) {
            if (!isInView) {
                return state.filter(function (item) { return item !== slide; }).sort(function (a, b) { return a - b; });
            }
            return Array.from(new Set(__spreadArray(__spreadArray([], state, true), [slide], false))).sort(function (a, b) { return a - b; });
        });
    };
    var handleInfiniteLoop = function (direction) {
        if (!carouselRef.current) {
            return;
        }
        setHasSnappingRule(false);
        var scrollWidth = carouselRef.current.scrollWidth;
        carouselRef.current.scrollTo({
            left: direction === 'next' ? 0 : scrollWidth,
            behavior: 'smooth'
        });
    };
    var onSlideChange = function (direction) {
        if (!carouselRef.current) {
            return;
        }
        setIsPointer(false);
        var slides = carouselRef.current.children;
        var offsetWidth = slides[0].offsetWidth;
        var currentScrollPosition = carouselRef.current.scrollLeft;
        var sign = direction === 'next' ? 1 : -1;
        carouselRef.current.scrollTo({
            left: currentScrollPosition + sign * offsetWidth,
            behavior: 'smooth'
        });
    };
    var handleImageZoom = function (e) {
        // checking for window adds support for SSR, where window is undefined on the server and causes errors in rendering
        if (typeof window !== 'undefined') {
            hive.track('ZOOM_LAUNCH');
        }
        var clickedImageId = e.currentTarget.dataset.id;
        if (imageIds[currentSlidesInView[0]] !== clickedImageId) {
            if (currentSlidesInView[0] !== images.length - 1) {
                onSlideChange('next');
            }
            else {
                onSlideChange('previous');
            }
            return;
        }
        var selectedImage = images.find(function (image) { return image.id.toString() === clickedImageId; });
        // put the selected image at the top
        var restImages = __spreadArray([], images, true);
        restImages.splice(restImages.indexOf(selectedImage), 1);
        setZoomedImages(__spreadArray([selectedImage], restImages, true));
        setZoomModalOpen(!isZoomModalOpen);
    };
    var handleTouchMove = function (e) {
        // note: we aware this would also launch with other 2 finger touches like a tap and we are ok with it
        var isPinched = e.targetTouches.length === 2;
        if (isPinched) {
            handleImageZoom(e);
        }
    };
    var carouselImages = images.map(function (image, index) { return (React.createElement(Styled.Image, { key: image.id || index, "data-id": image.id || index, background: convertColorToThemeHexCode('grey20'), resolution: { large: 1068 }, crop: {
            height: 3040,
            width: 3040
        }, src: image.src, alt: image.alt, role: index !== 0 ? 'presentation' : undefined, onClick: handleImageZoom, onTouchMove: handleTouchMove, onMouseEnter: function (e) {
            if (imageIds[currentSlidesInView[0]] !== e.currentTarget.dataset.id) {
                setIsPointer(true);
            }
        }, isLazy: index !== 0, withProgressiveLoading: index !== 0 })); });
    var imageInteractionTracker = useTrackInteractedSlide({
        current: currentSlidesInView[0],
        images: images
    });
    useEffect(function () {
        var _a;
        if (isCarouselRefLoaded) {
            (_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ left: 0 });
        }
        setSlideInView([0]);
        imageInteractionTracker.reset();
    }, [carouselImages.length, images[0].id]);
    useEffect(function () {
        setIsCarouselRefLoaded(!!carouselRef.current);
    }, [carouselRef]);
    return (React.createElement(Styled.ImageCarouselLargeContainer, null,
        React.createElement(Styled.ImageCarouselLarge, { ref: carouselRef, "data-testid": "ImageCarouselLarge", onMouseEnter: function () {
                setHasSnappingRule(true);
                if (carouselRef.current) {
                    carouselRef.current.focus({
                        preventScroll: true
                    });
                }
            }, onTouchStart: function () { return setHasSnappingRule(true); }, onKeyDown: function (e) {
                setHasSnappingRule(true);
                if (e.key === 'ArrowLeft' && currentSlidesInView[0] > 0) {
                    e.preventDefault();
                    onSlideChange('previous');
                }
                if (e.key === 'ArrowRight' &&
                    currentSlidesInView[0] < images.length - 1) {
                    e.preventDefault();
                    onSlideChange('next');
                }
            }, hasSnappingRule: hasSnappingRule, tabIndex: 0 }, carouselImages.map(function (image, index) { return (React.createElement(Styled.Content, { tabIndex: 0, key: index, isPointer: isPointer },
            React.createElement(InView, { parent: carouselRef.current, setElementInView: handleInViewChange, position: index, isLazy: false }, image))); })),
        images.length > 1 && (
        /**
         * offsetWidth is subtracted by 60 to find position of selectCarousel:
         * 136px(width of selectCarousel with numbers) - 16(gap width) / 2
         */
        React.createElement(Styled.SelectCarousel, { totalSlides: images.length, currentSlide: currentSlide, onLeftArrowClick: function () {
                onSlideChange('previous');
            }, onRightArrowClick: function () {
                onSlideChange('next');
            }, onInfiniteLoop: handleInfiniteLoop })),
        React.createElement(ProductImageZoom, { "data-testid": "image_zoom_modal", isOpen: isZoomModalOpen, images: zoomedImages, onRequestClose: function () { return setZoomModalOpen(false); } })));
};
export default ImageCarouselLarge;
